<template>
  <page-header title="Onboarding Slide" :identifier="id">
    <template v-if="isLoaded">
      <button v-if="id" class="btn-outline-primary ml-auto h-10 mr-2" @click="toggleEnableDisableSlide">
        <template v-if="slide.isActive">
          <LockClosedIcon class="size-5 -ml-1 mr-2" />
          Disable
        </template>
        <template v-else>
          <LockOpenIcon class="size-5 -ml-1 mr-2" />
          Enable
        </template>
      </button>
      <button class="btn-primary ml-auto h-10" :class="{'disabled': isSaving || !isDataChanged}" @click="save">
        <CircleStackIcon class="size-5 -ml-1 mr-2" />
        Save
      </button>
    </template>
  </page-header>

  <pre v-if="false">
    {{ JSON.stringify(slide, null, 2) }}
  </pre>

  <onboarding-slide-form v-if="isLoaded" ref="slideFormRef" :slide="slide" @save="save" />

  <div v-else>
    Loading...
  </div>
</template>

<script setup>
import { computed, ref, onMounted, onBeforeUnmount, toRaw } from 'vue';
import { onBeforeRouteLeave } from 'vue-router';
import PageHeader from '../components/PageHeader.vue';
import OnboardingSlideForm from '../components/OnboardingSlideForm.vue';
import {
    CircleStackIcon,
    LockOpenIcon,
    LockClosedIcon
  } from '@heroicons/vue/24/outline';
import { useFetch } from '@vueuse/core';
import { apiDomain } from '../composables/useConstants';
import { Modal } from '../composables/useModal';
import { useToast } from 'vue-toastification';
import hashSum from 'hash-sum';

const props = defineProps({
  id: {
    type: [ Number, String ],
    default: null
  }
});

const toast = useToast();
const slideFormRef = ref(null);
const isLoaded = ref(false);
const isSaving = ref(false);
const slide = ref({});
const slideHashSum = ref(null);

const isDataChanged = computed(() => slideHashSum.value !== hashSum(slide.value));

if (props.id) {
  load();
} else {
  slide.value = {
    title: '',
    description: '',
    question: '',
    checkboxes: [],
    image: null,
    order: 1,
    isActive: false,
    selectionType: 'multiple',
    isFinalSlide: false,
    finalSlideConfig: {
      title: '',
      description: '',
      shortDescription: '',
      image: null,
      showTelegramButton: true,
      showWhatsappButton: true,
      showTelegramQR: true,
      showWhatsappQR: true
    }
  };
  slideHashSum.value = hashSum(slide.value);
  isLoaded.value = true;
}

async function load() {
  const { data } = await useFetch(`${apiDomain}/api/onboarding/${props.id}`).get().json();
  slideHashSum.value = hashSum(data.value);
  
  // Convert null values to empty strings for final slides
  if (data.value) {
    slide.value = {
      ...data.value,
      title: data.value.title ?? '',
      description: data.value.description ?? '',
      question: data.value.question ?? '',
      checkboxes: data.value.checkboxes ?? []
    };
  }
  
  isLoaded.value = true;
}

async function save() {
  isSaving.value = true;

  if (slideFormRef.value) {
    const isValid = await slideFormRef.value.onValidate();
    if (!isValid) {
      isSaving.value = false;
      toast.error('Please fix form validation errors before saving');
      return;
    }
  }

  slide.value.isActive = slide.value.isActive === true ? true : false;

  const { data, error } = await useFetch(`${apiDomain}/api/onboarding/`).post(toRaw(slide.value)).json();

  isSaving.value = false;

  if (error.value) {
    toast.error(error.value);
  }

  if (data.value) {
    slideHashSum.value = hashSum(data.value);
    Object.assign(slide.value, data.value);
    toast.success('Onboarding slide saved successfully');
  }
}

async function toggleEnableDisableSlide() {
  const direction = slide.value.isActive ? 'disable' : 'enable';
  const { data } = await useFetch(`${apiDomain}/api/onboarding/${direction}/${slide.value.id}`).get().json();

  if (data.value?.success) {
    load();
    toast.success(`Onboarding slide ${direction}d successfully`);
    return;
  }

  toast.error('Error enabling onboarding slide');
}

function handleBeforeUnload(e) {
  if (!isDataChanged.value || !Object.keys(slide.value).length) {
    return;
  }
  e.preventDefault();
  e.returnValue = '';
}

onBeforeRouteLeave(async (to, from, next) => {
  if (!isDataChanged.value || !Object.keys(slide.value).length) {
    return next();
  }

  const confirm = await Modal.confirm({
    title: 'Unsaved changes',
    body: 'You have unsaved changes. <br/> If you leave this page, your changes will be lost. <br/> Are you sure you want to exit without saving?',
    okButton: 'Leave',
    iconColor: 'primary'
  });

  return confirm ? next() : next(false);
});

onMounted(() => {
  window.addEventListener('beforeunload', handleBeforeUnload);
});

onBeforeUnmount(() => {
  window.removeEventListener('beforeunload', handleBeforeUnload);
});
</script>
